import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Zap, Users, Trophy, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { attractions } from '../data/mock';
import { NewsletterSignup } from '../components/NewsletterSignup';
import { GoogleReviewsSection } from '../components/GoogleReviewsSection';
import { EventPackagesSection } from '../components/EventPackagesSection';
import { PartyPackagesSection } from '../components/PartyPackagesSection';
import SEO, { seoData } from '../components/SEO';

export const Home = ({ onBookNow }) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      <SEO {...seoData.home} />
      {/* Hero Section */}
      <section className="hero-section relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Cyberpunk background */}
        <div className="absolute inset-0 bg-gradient-to-br from-page via-card to-accent">
          <div className="absolute inset-0 cyber-grid"></div>
          <div className="absolute inset-0" style={{
            background: 'radial-gradient(circle at 20% 50%, rgba(0, 255, 65, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255, 0, 255, 0.1) 0%, transparent 50%), radial-gradient(circle at 50% 20%, rgba(0, 217, 255, 0.1) 0%, transparent 50%)'
          }}></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl">
            <div className="mb-6">
              <span className="inline-block px-4 py-2 bg-card border-2 border-blue rounded-full text-blue text-sm font-bold uppercase tracking-wider animate-pulse" style={{ boxShadow: '0 0 15px rgba(0, 217, 255, 0.3)' }}>
                7,500 SQ FT • 8 EPIC ATTRACTIONS • MICHIGAN CITY
              </span>
            </div>
            <h1 className="hero-title text-7xl md:text-8xl lg:text-9xl font-black uppercase mb-6 leading-tight">
              <span className="text-primary" style={{ textShadow: '0 0 20px var(--neon-green), 0 0 40px var(--neon-green)' }}>Br</span>
              <span className="text-pink" style={{ textShadow: '0 0 20px var(--neon-pink), 0 0 40px var(--neon-pink)' }}>8</span>
              <span className="text-blue" style={{ textShadow: '0 0 20px var(--neon-blue), 0 0 40px var(--neon-blue)' }}>k </span>
              <span className="text-primary" style={{ textShadow: '0 0 20px var(--neon-green), 0 0 40px var(--neon-green)' }}>FREE</span>
              <br/>
              <span className="text-blue" style={{ textShadow: '0 0 20px var(--neon-blue), 0 0 40px var(--neon-blue)' }}>FROM</span>
              <br/>
              <span className="text-pink" style={{ textShadow: '0 0 20px var(--neon-pink), 0 0 40px var(--neon-pink)' }}>REALITY</span>
            </h1>
            <p className="body-large text-white max-w-2xl mb-8 leading-relaxed text-lg">
              <span className="text-primary font-bold">DIVE INTO VR WORLDS.</span> <span className="text-blue font-bold">SMASH YOUR STRESS.</span> <span className="text-pink font-bold">GAME LIKE IT'S 1999.</span>
              <br/>
              7,500 sq ft facility featuring premium Zero Latency VR (1,053 sq ft arena), multiple 400 sq ft standard free-roam VR areas, 3 consoles with 40,000+ retro games each, and our legendary Br8k Room. This isn't just fun – it's an experience you'll never forget.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button onClick={onBookNow} className="btn-primary text-lg px-8 py-6 font-bold animate-pulse">
                BOOK YOUR ADVENTURE
                <ArrowRight className="ml-2" size={20} />
              </Button>
              <Button onClick={() => navigate('/attractions')} className="btn-secondary text-lg px-8 py-6 font-bold">
                SEE ALL ATTRACTIONS
              </Button>
            </div>
            <div className="flex items-center gap-6 mt-8 text-secondary">
              <div className="flex items-center gap-2">
                <Users className="text-primary" size={20} />
                <span className="text-sm font-semibold">GROUPS WELCOME</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="text-blue" size={20} />
                <span className="text-sm font-semibold">ALL AGES 12+</span>
              </div>
              <div className="flex items-center gap-2">
                <Trophy className="text-pink" size={20} />
                <span className="text-sm font-semibold">EPIC PARTIES</span>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-primary rounded-full flex items-start justify-center p-2" style={{ boxShadow: '0 0 10px var(--neon-green)' }}>
            <div className="w-1 h-3 bg-primary rounded-full" style={{ boxShadow: '0 0 5px var(--neon-green)' }}></div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-page relative">
        <div className="absolute inset-0 cyber-grid opacity-20"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="heading-2 text-5xl md:text-6xl font-black uppercase mb-4">
              <span className="text-primary" style={{ textShadow: '0 0 15px var(--neon-green)' }}>Why Br</span>
              <span className="text-pink" style={{ textShadow: '0 0 15px var(--neon-pink)' }}>8</span>
              <span className="text-blue" style={{ textShadow: '0 0 15px var(--neon-blue)' }}>k </span>
              <span className="text-primary" style={{ textShadow: '0 0 15px var(--neon-green)' }}>With Us?</span>
            </h2>
            <p className="body-medium text-secondary max-w-2xl mx-auto">
              We're not just another entertainment venue. We're a destination.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-card border-2 border-green rounded-2xl p-8 hover:border-blue transition-all duration-300 hover:scale-105 group" style={{ boxShadow: '0 0 15px rgba(0, 255, 65, 0.2)' }}>
              <div className="w-16 h-16 bg-gradient-to-br from-primary to-blue rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform" style={{ boxShadow: '0 0 20px rgba(0, 255, 65, 0.5)' }}>
                <Zap className="text-inverse" size={32} />
              </div>
              <h3 className="heading-5 text-primary font-bold mb-3 uppercase">Cutting-Edge Tech</h3>
              <p className="text-secondary text-sm leading-relaxed">
                Premium VR equipment and the latest gaming technology for unmatched immersion.
              </p>
            </div>

            <div className="bg-card border-2 border-blue rounded-2xl p-8 hover:border-pink transition-all duration-300 hover:scale-105 group" style={{ boxShadow: '0 0 15px rgba(0, 217, 255, 0.2)' }}>
              <div className="w-16 h-16 bg-gradient-to-br from-blue to-pink rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform" style={{ boxShadow: '0 0 20px rgba(0, 217, 255, 0.5)' }}>
                <Users className="text-inverse" size={32} />
              </div>
              <h3 className="heading-5 text-blue font-bold mb-3 uppercase">Family Friendly</h3>
              <p className="text-secondary text-sm leading-relaxed">
                Safe, inclusive environment perfect for ages 10+ and all skill levels.
              </p>
            </div>

            <div className="bg-card border-2 border-pink rounded-2xl p-8 hover:border-green transition-all duration-300 hover:scale-105 group" style={{ boxShadow: '0 0 15px rgba(255, 0, 255, 0.2)' }}>
              <div className="w-16 h-16 bg-gradient-to-br from-pink to-primary rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform" style={{ boxShadow: '0 0 20px rgba(255, 0, 255, 0.5)' }}>
                <Trophy className="text-inverse" size={32} />
              </div>
              <h3 className="heading-5 text-pink font-bold mb-3 uppercase">Competition Ready</h3>
              <p className="text-secondary text-sm leading-relaxed">
                Tournaments, leaderboards, and bragging rights for the ultimate competitive experience.
              </p>
            </div>

            <div className="bg-card border-2 border-blue rounded-2xl p-8 hover:border-primary transition-all duration-300 hover:scale-105 group" style={{ boxShadow: '0 0 15px rgba(0, 217, 255, 0.2)' }}>
              <div className="w-16 h-16 bg-gradient-to-br from-blue to-green rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform" style={{ boxShadow: '0 0 20px rgba(0, 217, 255, 0.5)' }}>
                <Clock className="text-inverse" size={32} />
              </div>
              <h3 className="heading-5 text-blue font-bold mb-3 uppercase">Flexible Hours</h3>
              <p className="text-secondary text-sm leading-relaxed">
                Open late on weekends, perfect for after-school fun or weekend adventures.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attractions Grid — all 8, direct-to-Roller */}
      <section className="py-24 bg-card relative" data-testid="home-attractions-grid">
        <div className="absolute inset-0 bg-gradient-to-b from-page to-accent opacity-50"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="heading-2 text-5xl md:text-6xl font-black uppercase mb-4">
              <span className="text-blue" style={{ textShadow: '0 0 15px var(--neon-blue)' }}>Choose Your </span>
              <span className="text-pink" style={{ textShadow: '0 0 15px var(--neon-pink)' }}>Adventure</span>
            </h2>
            <p className="body-medium text-secondary max-w-2xl mx-auto">
              Eight immersive experiences. Tap any attraction to reserve your session — pricing and scheduling are handled at checkout.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {attractions.map((attraction) => (
              <div
                key={attraction.id}
                data-testid={`home-attraction-${attraction.slug || attraction.id}`}
                className="bg-page border-2 border-green rounded-2xl overflow-hidden flex flex-col hover:border-pink transition-all duration-300 hover:-translate-y-1 group"
                style={{ boxShadow: '0 0 15px rgba(0, 255, 65, 0.2)' }}
              >
                <div className="h-40 bg-gradient-to-br from-page to-card flex items-center justify-center relative overflow-hidden border-b-2 border-green">
                  {attraction.photo ? (
                    <>
                      <img
                        src={attraction.photo}
                        alt={attraction.name}
                        className="absolute inset-0 w-full h-full object-cover"
                        style={{ objectPosition: attraction.photoPosition || 'center' }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-page/70 via-transparent to-transparent pointer-events-none"></div>
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-pink/5 group-hover:opacity-70 transition-opacity pointer-events-none"></div>
                    </>
                  ) : attraction.images && attraction.images.length > 0 ? (
                    <>
                      <div className="absolute inset-0 bg-gradient-to-br from-page/90 to-card"></div>
                      <div className="relative z-10 grid grid-cols-2 gap-1 p-2 w-full h-full">
                        {attraction.images.slice(0, 4).map((imgSrc, imgIdx) => (
                          <div key={imgIdx} className="rounded flex items-center justify-center overflow-hidden">
                            <img
                              src={imgSrc}
                              alt={`${attraction.name} logo ${imgIdx + 1}`}
                              className="w-full h-full object-contain p-1"
                            />
                          </div>
                        ))}
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-pink/5 group-hover:opacity-70 transition-opacity pointer-events-none"></div>
                    </>
                  ) : attraction.image ? (
                    <>
                      <div className="absolute inset-0 bg-gradient-to-br from-page/95 to-card"></div>
                      <img
                        src={attraction.image}
                        alt={attraction.name}
                        className="relative z-10 w-full h-full object-contain p-4"
                      />
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-pink/5 group-hover:opacity-70 transition-opacity pointer-events-none"></div>
                    </>
                  ) : (
                    <>
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-blue/10 to-pink/10"></div>
                      <div
                        className="relative z-10 text-primary text-xl md:text-2xl font-black uppercase opacity-40 group-hover:opacity-60 transition-opacity text-center px-4 leading-tight"
                        style={{ textShadow: '0 0 20px var(--neon-green)' }}
                      >
                        {attraction.name}
                      </div>
                    </>
                  )}
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="text-primary font-black text-lg uppercase mb-1 group-hover:text-blue transition-colors leading-tight">
                    {attraction.name}
                  </h3>
                  <p className="text-pink text-xs italic mb-3 font-semibold">{attraction.tagline}</p>
                  <p className="text-secondary text-xs leading-relaxed mb-4 flex-1">{attraction.description}</p>
                  <Button
                    onClick={onBookNow}
                    className="btn-primary w-full font-bold text-sm mt-auto"
                    data-testid={`home-book-${attraction.slug || attraction.id}`}
                  >
                    Book Now
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-page relative overflow-hidden">
        <div className="absolute inset-0 cyber-grid opacity-30"></div>
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(circle at 30% 50%, rgba(0, 255, 65, 0.15) 0%, transparent 40%), radial-gradient(circle at 70% 50%, rgba(255, 0, 255, 0.15) 0%, transparent 40%)'
        }}></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="heading-2 text-5xl md:text-6xl font-black uppercase mb-6 leading-tight">
              <span className="text-primary" style={{ textShadow: '0 0 20px var(--neon-green)' }}>Ready to Br</span>
              <span className="text-pink" style={{ textShadow: '0 0 20px var(--neon-pink)' }}>8</span>
              <span className="text-blue" style={{ textShadow: '0 0 20px var(--neon-blue)' }}>k </span>
              <span className="text-primary" style={{ textShadow: '0 0 20px var(--neon-green)' }}>Reality?</span>
            </h2>
            <p className="body-large text-white mb-8">
              Book your experience today and discover why we're Michigan City's favorite entertainment destination.
            </p>
            <Button onClick={onBookNow} className="btn-primary text-lg px-10 py-6 font-bold">
              Book Now
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      {/* Birthday Party Packages */}
      <PartyPackagesSection onBookNow={onBookNow} />

      {/* Testimonials Section */}
      <GoogleReviewsSection />

      {/* Birthday Party Section */}
      <EventPackagesSection onBookNow={onBookNow} />

      {/* Newsletter Signup */}
      <NewsletterSignup />
    </div>
  );
};